import { type ReactNode, useEffect, useState } from 'react';

import { cn } from '@/lib/utils';

import { Spinner } from '../Spinner';

type SpinnerSize = React.ComponentProps<typeof Spinner>['size'];

export type SpinnerOverlayProps = {
  isSpinning: boolean;
  children?: ReactNode;
  delay?: number;
  size?: SpinnerSize;
  hideContent?: boolean;
  className?: string;
};

const SpinnerOverlay = ({
  isSpinning,
  children,
  delay = 0,
  size = 'md',
  hideContent = false,
  className,
}: SpinnerOverlayProps) => {
  const [showSpinner, setShowSpinner] = useState(delay === 0 ? true : false);

  useEffect(() => {
    if (!isSpinning) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setShowSpinner(false);
      return;
    }
    const id = setTimeout(() => setShowSpinner(true), delay);
    return () => clearTimeout(id);
  }, [isSpinning, delay]);

  return (
    <div className={cn('relative', className)}>
      <div
        className={cn(
          'transition-opacity duration-200',
          hideContent && showSpinner ? 'invisible' : isSpinning ? 'opacity-50' : 'opacity-100',
          children != null && children !== '' ? 'flex min-h-0 flex-1 flex-col' : undefined,
        )}
      >
        {children ?? <div className="min-h-8 min-w-8" />}
      </div>
      {showSpinner && (
        <div className="absolute inset-0">
          <div
            className="absolute left-1/2 -translate-1/2"
            style={{ top: 'min(50%, 200px)' }}
          >
            <Spinner variant="spoke" size={size} />
          </div>
        </div>
      )}
    </div>
  );
};

export default SpinnerOverlay;
